import { Component } from "@angular/core";
import { take } from "rxjs/operators";
import { TranslocoService } from "@ngneat/transloco";
import { MatDialog } from "@angular/material/dialog";
import { LanguageOptions } from "../../../../common";
import { BlockBaseComponent } from "../block-base/block-base.component";
import { StoreSelectorsService } from "../../../../services";
import { actionUpdateEventsStrings } from "../../../../store/actions/user.actions";
import { DialogEventsExplorerComponent } from "../../../events/dialog-events-explorer/dialog-events-explorer.component";
import { IEventBlock } from "../../../../interfaces";
import { ITableListColumn, ITableListEvent } from "../../../../components/table-list-input/table-list-input.component";
import { IButtonBar } from "../../../../components/add-remove-bar/button-bar.component";

@Component({
    selector: "app-block-data-provider",
    templateUrl: "./block-data-provider.component.html",
    styleUrls: ["./block-data-provider.component.scss"],
})

export class BlockDataProviderComponent extends BlockBaseComponent {
    eventParams = [];
    assignedEventParam;
    sourceType;
    tableColumns: Array<ITableListColumn> = [];
    buttons: Array<IButtonBar> = [];

    constructor(private translocoService: TranslocoService, public override storeSelector: StoreSelectorsService, public override dialog: MatDialog) {
        super(storeSelector, dialog);
        this.initAiTableConfig();
    }

    override submit(formValues) {
        if (!Array.isArray(formValues.Prompt) || !Array.isArray(formValues.Description)) {
            formValues.Prompt = [formValues.Prompt || formValues.Description];
        }
        return super.submit(formValues, null, true, true);
    }

    override componentInit() {
        super.componentInit();
        this.assignedEventParam = this.sceneItem.Data[0]?.DataProvider?.[0]?.$.eventName;
    }

    initAiTableConfig() {
        this.tableColumns = [
            {
                inputType: "edit",
                name: "name",
                maxStringSize: "16",
            },
            {
                inputType: "edit",
                name: "description",
                maxStringSize: "50",
            },
        ];
        this.buttons = [
            {
                callBack: (v) => v,
                icon: "add",
                name: "add",
                disableByList: false,
                disabled: false,
                tooltip: this.translocoService.translate("general.add", {}, this.translocoService.getActiveLang()),
            },
            {
                callBack: (v) => v,
                icon: "remove",
                name: "remove",
                confirmRemove: this.translocoService.translate("toast.deleteConfirm", {}, this.translocoService.getActiveLang()),
                disableByList: true,
                disabled: false,
                tooltip: this.translocoService.translate("general.remove", {}, this.translocoService.getActiveLang()),
            },
        ];
    }

    override componentStoreUpdates() {
        this.updateUIStatus(this.sceneItem.Data[0]?.DataProvider?.[0]?.$.source);
        if (this.sceneItemSwitched) {
            this.reloadForm();
        }
        super.componentStoreUpdates();
        super.initApplyStoreToDynamic(true);
    }

    updateUIStatus(source) {
        this.checkAISource(source);
        this.sourceType = source;

        this.setUiConfigHide("eventName", source === "ai");
        this.setUiConfigHide("itemsPath", source === "ai");
        this.setUiConfigHide("Fields", source !== "ai");
        this.setUiConfigHide("language", source !== "ai");
        this.setUiConfigHide("Prompt", source !== "ai");

        this.setUiConfigHide("resultEventName", ["cloud", "drive"].indexOf(source) == -1);
        this.setUiConfigHide("resultEventParam", ["cloud", "drive"].indexOf(source) == -1);
        this.setUiConfigHide("url", ["external", "ifttt", "stringify"].indexOf(source) == -1);
    }

    override initRegisterDynamic() {
        this.uiConfigInit = [
            {
                type: "select",
                label: this.translocoService.translate("general.source", {}, this.translocoService.getActiveLang()),
                name: "source",
                value: "cloud",
                // callBackEvent: (event) => this.changeSource(event),
                // detectChange: true,
                options: ["ai", "cloud", "drive", "runtime", "external", "ifttt", "stringify"],
                storePath: `Data[0].DataProvider[0].$.source`,
            },
            {
                type: "input",
                value: "",
                label: this.translocoService.translate("general.eventName", {}, this.translocoService.getActiveLang()),
                inputType: "text",
                textSideButton: {
                    icon: "notification_add",
                    callBackSideButton: (e, value) => {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        this.openEventsExplorerDialog("eventName", value);
                    },
                },
                // observable: this.storeSelector.getEventsCombined(),
                callBackValue: (item, direction) => this.dispatchEventsAll(),
                name: "eventName",
                storePath: `Data[0].DataProvider[0].$.eventName`,
            },
            {
                type: "input",
                value: "",
                label: this.translocoService.translate("general.resultEventName2", {}, this.translocoService.getActiveLang()),
                inputType: "text",
                textSideButton: {
                    icon: "notification_add",
                    callBackSideButton: (e, value) => {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        this.openEventsExplorerDialog("resultEventName", value);
                    },
                },
                // observable: this.storeSelector.getEventsCombined(),
                callBackValue: (item, direction) => this.dispatchEventsAll(),
                name: "resultEventName",
                storePath: `Data[0].DataProvider[0].$.resultEventName`,
            },
            {
                type: "input",
                value: "",
                label: this.translocoService.translate("general.resultEventParam", {}, this.translocoService.getActiveLang()),
                inputType: "text",
                name: "resultEventParam",
                storePath: `Data[0].DataProvider[0].$.resultEventParam`,
            },
            {
                type: "input",
                value: "",
                label: this.translocoService.translate("general.link", {}, this.translocoService.getActiveLang()),
                inputType: "text",
                name: "url",
                storePath: `Data[0].DataProvider[0].$.url`,
            },
            {
                type: "input",
                value: "",
                label: this.translocoService.translate("general.itemsPath", {}, this.translocoService.getActiveLang()),
                inputType: "text",
                name: "itemsPath",
                storePath: `Data[0].DataProvider[0].$.itemsPath`,
            },
            {
                type: "select",
                label: this.translocoService.translate("ai.language", {}, this.translocoService.getActiveLang()),
                name: "language",
                value: "en",
                ignoreMissingValue: true,
                storePath: `Data[0].DataProvider[0].Ai[0].$.language`,
                options: LanguageOptions,
            },
            {
                type: "input",
                value: "",
                label: this.translocoService.translate("ai.prompt", {}, this.translocoService.getActiveLang()),
                inputType: "textarea",
                name: "Prompt",
                ignoreMissingValue: true,
                storePath: `Data[0].DataProvider[0].Ai[0].Prompt`,
            },
            {
                type: "input",
                label: "",
                name: "Fields",
                inputType: "tablelist",
                tableColumns: this.tableColumns,
                cloneVals: true,
                multiSelect: false,
                rememberSelection: false,
                allowFilter: false,
                disableSubmit: false,
                expandableEditor: true,
                enablePagination: false,
                buttonBarClassName: "blockJsonButtonBar",
                buttons: this.buttons,
                ignoreMissingValue: true,
                inputEditFormat: { name: "text", description: "text" },
                inputEditRenamePath: [
                    { path: "$.name", key: "name" },
                    { path: "$.description", key: "description" },
                ],
                storePath: `Data[0].DataProvider[0].Ai[0].Fields[0].Field`,
                callBackEvent: (v) => this.onTableListChangeCallBack(v),
                // callBackValue: this.onCallBackValue(),
            },

            // {
            //     type: "input",
            //     value: '',
            //     label: this.translocoService.translate('general.metaData', {}, this.translocoService.getActiveLang()),
            //     inputType: "text",
            //     name: "metaData",
            //     storePath: `Data[0].DataProvider[0].$.metaData`,
            //     hideMe: ["external", "ifttt", "stringify"].indexOf(source) == -1
            // },
        ];
    }

    private onTableListChangeCallBack(e: ITableListEvent) {
        if (e.currentAction === "drop" || e.currentAction === "removed" || e.currentAction === "rename") {
            this.dispatchSceneItemChanges({
                sceneId: this.sceneId,
                sceneItemId: this.sceneItem.$.id,
                values: [
                    {
                        key: "Field",
                        value: e.values,
                        storePath: "Data[0].DataProvider[0].Ai[0].Fields[0]",
                    },
                ],
            });
        } else if (e.currentAction === "added") {
            // Add new row default values
            const newItem = {
                $: {
                    name: "text1",
                    description: "title on the request",
                },
            };

            const values = e.values || [];
            this.dispatchSceneItemChanges({
                sceneId: this.sceneId,
                sceneItemId: this.sceneItem.$.id,
                values: [
                    {
                        key: "Field",
                        value: [...values, newItem],
                        storePath: "Data[0].DataProvider[0].Ai[0].Fields[0]",
                    },
                ],
            });
        }
    }

    onCallBackValue() {
        return (item, direction) => {
            switch (direction) {
                case "IN": {
                    item.$ = {
                        name: item?.Field?.[0]?.$?.name,
                        description: item?.Field?.[0]?.$?.description,
                    };
                    return item;
                }
                case "OUT": {
                    // item.Field[0] = item;
                    item.Field[0].$.name = item.$.name;
                    item.Field[0].$.description = item.$.description;
                    delete item.$;
                    return item;
                }
            }
        };
    }

    // onChangeEventParam(value) {
    //     let key = 'eventName';
    //     let storePath = 'Data["0"].DataProvider["0"].$';

    //     const block = DataProviderBlocks.find((b) => compareIds(this.sceneItem.$.player, b.id));

    //     switch (block.id) {
    //         case this.blockLabelRecords.JSON_VIEWER.id: {
    //         }
    //         case this.blockLabelRecords.JSON_PLAYER.id: {
    //         }
    //         case this.blockLabelRecords.STOCKS.id: {
    //         }
    //         default: {
    //             break;
    //         }
    //     }

    //     this.dispatchSceneItemChanges({
    //         sceneId: this.sceneId,
    //         sceneItemId: this.sceneItem.$.id,
    //         values: [{
    //             key,
    //             value,
    //             storePath
    //         }]
    //     });
    // }

    checkAISource(source) {
        if (this.sourceType !== source && source === "ai") {
            if (!this.sceneItem.Data[0].DataProvider[0].Ai) {
                this.dispatchSceneItemChanges({
                    sceneId: this.sceneId,
                    sceneItemId: this.sceneItem.$.id,
                    values: [
                        {
                            key: "Ai",
                            value: this.getDefaultAi(),
                            storePath: "Data[0].DataProvider[0]",
                            cloneValue: true,
                        },
                    ],
                });
            }
        }
    }

    private getDefaultAi() {
        return [
            {
                $: {
                    language: "en",
                },
                Prompt: [""],
                Fields: [
                    {
                        Field: [
                            {
                                $: {
                                    name: "text1",
                                    description: "title on the request",
                                },
                            },
                        ],
                    },
                ],
            },
        ];
    }

    openEventsExplorerDialog(payload, value) {
        const data: IEventBlock = {
            eventName: value.name,
            eventBlockId: this.sceneItemId,
            blockName: this.sceneItem.$.label,
            eventSceneId: this.sceneId,
            eventType: "",
            sceneName: "",
        };
        const dialogRefVal = this.dialog.open(DialogEventsExplorerComponent, {
            width: "70%",
            data,
        });
        this.cod(
            dialogRefVal
                .afterClosed()
                .pipe(take(1))
                .subscribe((result: IEventBlock) => {
                    if (!result) return;
                    this.setFormValue(result.payload.eventName, result.eventName);
                    this.submit(this.form.value);
                }),
        );
    }

    dispatchEventsAll() {
        const eventsAll = [];
        if (this.sceneItem.Data[0]?.DataProvider?.[0]?.$?.resultEventName) {
            eventsAll.push(this.sceneItem.Data[0].DataProvider[0].$.resultEventName);
        }

        if (this.sceneItem.Data[0]?.DataProvider?.[0]?.$?.eventName) {
            eventsAll.push(this.sceneItem.Data[0].DataProvider[0].$.eventName);
        }

        if (eventsAll.length > 0) {
            this.storeSelector.dispatch(actionUpdateEventsStrings({ eventsStrings: eventsAll }));
        }
    }
}
